import { useRef, useState } from 'react'

import useReactionModal from '@web/components/ReactionArea/ReactionModal/useReactionModal'
import { ReactionArea_ReviewGroupFragment } from '@web/gql/graphql'
import useMe from '@web/hooks/useMe'
import useOutsideClick from '@web/hooks/useOutsideClick'
import { useReactionTooltip } from '@web/hooks/useReactionTooltip'
import { reactionTypeMap } from '@web/types/reaction-type'

interface Props {
  reviewGroup: ReactionArea_ReviewGroupFragment
  checkOverViewport?: boolean
}

export const useReaction = ({ reviewGroup, checkOverViewport = false }: Props) => {
  const { reactionStat } = reviewGroup
  const { me } = useMe()

  const [showReactionLayer, setShowReactionLayer] = useState(false)
  const layerRef = useRef<HTMLDivElement>(null)
  const openLayerButtonRef = useRef<HTMLButtonElement>(null)

  const closeReactionLayer = () => {
    setShowReactionLayer(false)
  }

  const reactionCounts = reactionStat?.reactionCounts ?? []
  const hasReaction = !!(reactionStat?.totalCount && reactionStat.reactionCounts.length)

  const hasMyReaction = !!reviewGroup.myReactionType

  const { ref, showReactionTooltip, closeTooltip } = useReactionTooltip({
    reviewGroupId: reviewGroup.id,
    skip: !hasReaction || hasMyReaction,
  })

  useOutsideClick({
    innerRef: layerRef,
    useHooks: showReactionLayer,
    callback: closeReactionLayer,
  })

  const { openReactionModal, isReactionModalOpen } = useReactionModal()

  const reactionTopTwo = reactionCounts.slice(0, 2).map(reactionCount => ({
    emojiUrl: reactionTypeMap.get(reactionCount.reactionType)?.emojiUrl,
    name: reactionTypeMap.get(reactionCount.reactionType)?.name,
  }))

  const toggleReactionLayer = () => {
    setShowReactionLayer(!showReactionLayer)
    if (showReactionTooltip) {
      closeTooltip()
    }
  }

  const totalReactionCount = reactionStat?.totalCount ?? 0
  const totalReactionCountWithoutMine = hasMyReaction ? totalReactionCount - 1 : totalReactionCount

  const isLayerOutOfViewport = () => {
    if (!checkOverViewport || !openLayerButtonRef.current) return false

    const MIN_MARGIN = 100
    const bottomOfLayer = openLayerButtonRef.current.getBoundingClientRect().bottom
    const isOutOfViewport = bottomOfLayer + MIN_MARGIN > window.innerHeight
    if (isOutOfViewport) return true

    return false
  }

  return {
    me,
    ref,
    openReactionModal,
    isReactionModalOpen,
    reactionTopTwo,
    toggleReactionLayer,
    isLayerOutOfViewport,
    totalReactionCountWithoutMine,
    showReactionTooltip,
    openLayerButtonRef,
    showReactionLayer,
    hasMyReaction,
    hasReaction,
    layerRef,
    closeReactionLayer,
  }
}
